import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { bugReportApi } from "../../../modules/bugreport/api";
import { CreateAutomaticFrontendErrorLog, CreateUserReportedBug } from "../../../modules/bugreport/types";
import type { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";

export const BUG_REPORT_QUERY_KEYS = {
  getBugReport: (id: UUID) => ["getBugReport", id],
  createAutomaticFrontendErrorLogBugReport: () => ["createAutomaticFrontendErrorLogBugReport"],
  createUserReportedBugReport: () => ["createUserReportedBugReport"],
  addBugReportAttachments: () => ["addBugReportAttachments"],
};

export const useGetBugReport = (id: UUID, enabled: boolean) => {
  return useQuery({
    queryKey: BUG_REPORT_QUERY_KEYS.getBugReport(id),
    queryFn: async () => {
      const { data } = await bugReportApi.getBugReport({ id });

      return data;
    },
    enabled,
  });
};

export const useCreateAutomaticFrontendErrorLogBugReport = () => {
  return useMutation({
    mutationKey: BUG_REPORT_QUERY_KEYS.createAutomaticFrontendErrorLogBugReport(),
    mutationFn: async (payload: CreateAutomaticFrontendErrorLog) => {
      const { data } = await bugReportApi.createAutomaticFrontendErrorLogBugReport(payload);

      return data;
    },
  });
};

export const useAddBugReportAttachments = () => {
  return useMutation({
    mutationKey: BUG_REPORT_QUERY_KEYS.addBugReportAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await bugReportApi.addBugReportAttachments(payload);

      return data;
    },
  });
};

export const useCreateUserReportedBugReport = () => {
  return useMutation({
    mutationKey: BUG_REPORT_QUERY_KEYS.createUserReportedBugReport(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUserReportedBug) => {
      const { data } = await bugReportApi.createUserReportedBugReport(payload);

      return data;
    },
    onSuccess: () => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("bugReport.helpers.reportCreated"),
      });
    },
  });
};
